import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock, Calculator, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import { POSTS } from "@/constants/blog";
import { MEDIA } from "@/constants/business";

export default function BlogPage() {
  return (
    <>
      <SEO
        title="Elevator & Escalator Blog | Guides, Insights & Industry Tips | Vila Elevators"
        description="In-depth guides on choosing elevators, maintenance costs, modernization ROI, and home lift installation — from Vila Elevators' engineering team."
        path="/blog"
        keywords="elevator blog india, lift maintenance blog, elevator buying guide, escalator articles bangalore"
      />

      <PageHeader
        eyebrow="Blog & Insights"
        title="Vertical transportation, decoded."
        desc="Practical guides from our engineering team — helping builders, architects and homeowners make better elevator decisions."
        image={MEDIA.productElevator}
      />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Interactive Tool Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="calc-highlight-card"
            className="relative overflow-hidden border border-[#b08d57]/40 bg-gradient-to-br from-[#b08d57]/60 via-[#faf7f2] to-[#faf7f2] p-8 lg:p-12 mb-16"
          >
            <div className="absolute inset-0 bg-grid opacity-40" />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 border border-[#b08d57]/60 bg-[#b08d57]/10 px-4 py-2 mb-6">
                  <Sparkles className="w-3 h-3 text-[#b08d57]" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57]">
                    New · Interactive Tool
                  </span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#1a1a1a] leading-[1.05] mb-4">
                  Elevator Cost<br />
                  <span className="text-[#b08d57]">Calculator</span>
                </h2>
                <p className="text-[#4a4a4a] text-lg leading-relaxed mb-6 max-w-md">
                  Answer 4 quick questions — building type, floors, capacity, finish — and get a data-driven price range for your elevator in seconds.
                </p>
                <Link
                  to="/blog/elevator-cost-calculator"
                  data-testid="calc-highlight-cta"
                  className="inline-flex items-center gap-2 bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors"
                >
                  Try The Calculator <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="hidden lg:flex justify-end">
                <div className="w-64 h-64 rounded-full bg-[#b08d57]/20 flex items-center justify-center border border-[#b08d57]/40">
                  <Calculator className="w-32 h-32 text-[#b08d57]" strokeWidth={1} />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Featured post */}
          {POSTS[0] && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20 border border-[#e5ddd0] bg-[#faf7f2]/40 hover:border-[#b08d57]/50 transition-colors"
            >
              <Link to={`/blog/${POSTS[0].slug}`} className="relative aspect-[16/10] lg:aspect-auto overflow-hidden group">
                <img src={POSTS[0].image} alt={POSTS[0].title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-4 left-4 bg-[#b08d57] text-white text-[10px] uppercase font-bold tracking-widest px-3 py-1">Featured · {POSTS[0].category}</div>
              </Link>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-xs text-[#8a8a8a] uppercase tracking-widest mb-4">
                  <span className="flex items-center gap-2"><Calendar className="w-3 h-3" /> {formatDate(POSTS[0].date)}</span>
                  <span className="flex items-center gap-2"><Clock className="w-3 h-3" /> {POSTS[0].readTime}</span>
                </div>
                <Link to={`/blog/${POSTS[0].slug}`}>
                  <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] leading-tight mb-4 hover:text-[#b08d57] transition-colors">
                    {POSTS[0].title}
                  </h2>
                </Link>
                <p className="text-[#6b6b6b] leading-relaxed mb-6">{POSTS[0].excerpt}</p>
                <Link to={`/blog/${POSTS[0].slug}`} data-testid={`blog-featured-read-${POSTS[0].slug}`} className="inline-flex items-center gap-2 text-[#b08d57] hover:text-[#b08d57] font-semibold uppercase text-xs tracking-wider group">
                  Read Article <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          )}

          {/* Grid of other posts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {POSTS.slice(1).map((p) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                data-testid={`blog-card-${p.slug}`}
                className="group border border-[#e5ddd0] bg-[#faf7f2]/40 hover:border-[#b08d57]/50 transition-colors flex flex-col"
              >
                <Link to={`/blog/${p.slug}`} className="relative aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute top-4 left-4 bg-[#faf7f2]/90 text-[#b08d57] text-[10px] uppercase font-bold tracking-widest px-3 py-1 border border-[#b08d57]/40">{p.category}</div>
                </Link>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-[10px] text-[#8a8a8a] uppercase tracking-widest mb-3">
                    <span>{formatDate(p.date)}</span>
                    <span>·</span>
                    <span>{p.readTime}</span>
                  </div>
                  <Link to={`/blog/${p.slug}`}>
                    <h3 className="font-display text-xl font-bold text-[#1a1a1a] leading-tight mb-3 hover:text-[#b08d57] transition-colors">
                      {p.title}
                    </h3>
                  </Link>
                  <p className="text-[#6b6b6b] text-sm leading-relaxed mb-4 flex-1">{p.excerpt}</p>
                  <Link to={`/blog/${p.slug}`} className="inline-flex items-center gap-2 text-[#b08d57] hover:text-[#b08d57] font-semibold uppercase text-[10px] tracking-widest">
                    Read More <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
